import { composeStruct } from '@strings';
import { expectDefined, NumberSchema, type Interpable, type Lerpable } from '@utils';
import { Bezier } from 'bezier-js';
import { z } from 'zod';
import { Angle } from './Angle';
import type { IPath, ParamPoint } from './IPath';
import { projectPointOntoLine, type LineSegment } from './LineSegment';
import { Point, pointArraysAreEqual } from './Point';
import { Rect } from './Rect';
import { point, rect } from './constructors';
import { composeSvgPathData, tokenizeSvgPathData } from './svgUtils';

// #region Schame

export const BezierCurveSchema = z
  .array(NumberSchema)
  .length(8)
  .transform(points =>
    bezier([
      point(points[0], points[1]),
      point(points[2], points[3]),
      point(points[4], points[5]),
      point(points[6], points[7])
    ])
  );

// #endregion

export function bezier(points: [Point, Point, Point, Point]): BezierCurve {
  return new BezierCurve(
    points.map(p => point(p.x, p.y)) as [Point, Point, Point, Point]
  );
}

export class BezierCurve implements IPath, Lerpable, Interpable {
  // Control points in order: start, start control, end control, end.
  readonly #points: [Point, Point, Point, Point];

  #cache:
    | {
        bezier?: Bezier;
        length?: number;
        bounds?: Rect;
        lut?: Point[];
      }
    | undefined;

  recalculateCache() {
    this.#cache = {
      length: this._length,
      bounds: this._bounds,
      bezier: this._bezier,
      lut: this._lut
    };
  }

  static LUT_INTERVAL = 10;
  static LUT_MIN_SAMPLES = 5;

  // #region Constructors

  constructor(points: [Point, Point, Point, Point]) {
    this.#points = points;
  }

  private fromBbox(bbox: {
    x: { min: number; max: number };
    y: { min: number; max: number };
  }): Rect {
    return rect(bbox.x.min, bbox.y.min, bbox.x.max, bbox.y.max);
  }

  // #endregion

  // #region Attributes

  get points(): [Point, Point, Point, Point] {
    return this.#points;
  }

  // #endregion

  // #region Comparison

  equals(other: BezierCurve): boolean {
    return this === other || pointArraysAreEqual(this.points, other.points);
  }

  isSamePath(other: IPath): boolean {
    return other instanceof BezierCurve && this.equals(other);
  }

  isIsomorphicTo(other: IPath): boolean {
    return other instanceof BezierCurve;
  }

  // #endregion

  // #region Geometric operations

  get start(): Point {
    return this.points[0];
  }

  get end(): Point {
    return this.points[3];
  }

  private get _length(): number {
    return this.bezier.length();
  }

  get length(): number {
    if (this.#cache?.length === undefined) {
      const length = this._length;
      this.#cache = { ...(this.#cache ?? {}), length };
    }
    return expectDefined(this.#cache.length);
  }

  private get _bezier(): Bezier {
    return new Bezier(
      this.points[0].x,
      this.points[0].y,
      this.points[1].x,
      this.points[1].y,
      this.points[2].x,
      this.points[2].y,
      this.points[3].x,
      this.points[3].y
    );
  }

  private get bezier(): Bezier {
    if (this.#cache?.bezier === undefined) {
      const b = this._bezier;
      this.#cache = { ...(this.#cache ?? {}), bezier: b };
    }
    return expectDefined(this.#cache.bezier);
  }

  private get _bounds(): Rect {
    return this.fromBbox(this.bezier.bbox());
  }

  get bounds(): Rect {
    if (this.#cache?.bounds === undefined) {
      const bounds = this._bounds;
      this.#cache = { ...(this.#cache ?? {}), bounds };
    }
    return expectDefined(this.#cache.bounds);
  }

  private get _lut(): Point[] {
    const numSamples = Math.max(
      Math.floor(this.length / BezierCurve.LUT_INTERVAL),
      BezierCurve.LUT_MIN_SAMPLES
    );
    return this.bezier.getLUT(numSamples).map(p => point(p.x, p.y));
  }

  get lut(): Point[] {
    if (this.#cache?.lut === undefined) {
      const lut = this._lut;
      this.#cache = { ...(this.#cache ?? {}), lut };
    }
    return expectDefined(this.#cache.lut);
  }

  reversed(): this {
    return bezier([this.end, this.points[2], this.points[1], this.start]) as this;
  }

  flattened(flatness: number): ParamPoint[] {
    const result: ParamPoint[] = [{ p: this.points[0], t: 0 }];
    this.flatten(
      this.points[0],
      this.points[1],
      this.points[2],
      this.points[3],
      0,
      1,
      flatness,
      result
    );
    return result;
  }

  private flatten(
    p0: Point,
    p1: Point,
    p2: Point,
    p3: Point,
    t0: number,
    t1: number,
    tolerance: number,
    result: ParamPoint[]
  ) {
    // Compute the maximum deviation from the curve to the line segment p0-p3
    const deviation = Math.max(
      projectPointOntoLine(p1, p0, p3).d,
      projectPointOntoLine(p2, p0, p3).d
    );

    if (deviation < tolerance) {
      result.push({ p: p3, t: t1 });
    } else {
      // De Casteljau subdivision
      const tMid = (t0 + t1) / 2;
      const p01 = p0.lerp(0.5, p1);
      const p12 = p1.lerp(0.5, p2);
      const p23 = p2.lerp(0.5, p3);
      const p012 = p01.lerp(0.5, p12);
      const p123 = p12.lerp(0.5, p23);
      const p0123 = p012.lerp(0.5, p123);

      // Recursively flatten the two subdivided curves
      this.flatten(p0, p01, p012, p0123, t0, tMid, tolerance, result);
      this.flatten(p0123, p123, p23, p3, tMid, t1, tolerance, result);
    }
  }

  // Calculate the derivative of the Bezier curve at t
  derivative(t: number): Point {
    const d = this.bezier.derivative(t);
    return point(d.x, d.y);
  }

  normal(t: number): Point {
    const n = this.bezier.normal(t);
    return point(n.x, n.y);
  }

  angle(t: number): Angle {
    return this.derivative(t).angleWithOrigin;
  }

  project(p: Point): { p: Point; t: number; d: number } {
    const result = this.bezier.project(p);
    return {
      p: point(result.x, result.y),
      t: expectDefined(result.t),
      d: expectDefined(result.d)
    };
  }

  /**
   * Determines whether this line Bezier curve intersects the given rectangle.
   *
   * @param r The rectangle.
   *
   * @returns True if this Bezier curve intersects the rectangle, false otherwise.
   */
  intersectsRect(r: Rect): boolean {
    // First, check if either endpoint is inside the rectangle
    if (r.containsPoint(this.start) || r.containsPoint(this.end)) {
      return true;
    }

    // If bounding boxes do not overlap, no intersection is possible
    if (!r.intersects(this.bounds)) {
      return false;
    }

    // Check for containment of interpolated points
    const expandedRect = r.expandingBy(BezierCurve.LUT_INTERVAL);
    if (this.lut.some(p => expandedRect.containsPoint(p))) {
      return true;
    }

    // No intersection
    return false;
  }

  /**
   * Whether this Bezier curve intersects the given line segment.
   *
   * @param line The line segment.
   *
   * @returns Whether this Bezier curve intersects the given line segment.
   */
  intersectsLineSegment(line: LineSegment): boolean {
    return this.intersectionsWithLineSegment(line).length > 0;
  }

  /**
   * The intersections of the Bezier curve with the given line segment.
   *
   * @param line The line segment.
   *
   * @returns The parameter values of the intersections.
   */
  intersectionsWithLineSegment(line: LineSegment): number[] {
    return this.bezier.lineIntersects({
      p1: { x: line.p1.x, y: line.p1.y },
      p2: { x: line.p2.x, y: line.p2.y }
    });
  }

  // #endregion

  // #region Interpolation

  // Returns a BezierCurve interpolated between this BezierCurve and the given
  // end BezierCurve.
  lerp(t: number, end: this): this {
    return bezier([
      this.points[0].lerp(t, end.points[0]),
      this.points[1].lerp(t, end.points[1]),
      this.points[2].lerp(t, end.points[2]),
      this.points[3].lerp(t, end.points[3])
    ]) as this;
  }

  // Returns a function that interpolates between this BezierCurve and the
  // given end BezierCurve.
  interp(end: this): (t: number) => this {
    return (t: number) => this.lerp(t, end);
  }

  // Returns a point interpolated along the Bezier curve.
  pointAt(t: number): Point {
    const p = this.bezier.get(t);
    return point(p.x, p.y);
  }

  lengthAt(t: number): number {
    return this.length * t;
  }

  // Returns `count` number of points interpolated along the Bezier curve.
  lerpPoints(count: number): Point[] {
    const points = [];
    for (let i = 0; i < count; i++) {
      points.push(this.pointAt(i / (count - 1)));
    }
    return points;
  }

  // Returns `count` number of points interpolated along the Bezier curve, not
  // including the endpoints.
  divisionPoints(count: number): Point[] {
    const points = [];
    for (let i = 1; i < count; i++) {
      points.push(this.pointAt(i / count));
    }
    return points;
  }

  // #endregion

  // #region Serialization

  // Serialize as a flat array of eight numbers [x1, y1, x2, y2...].
  toJSON(): unknown {
    return this.points.flatMap(p => [p.x, p.y]);
  }

  static fromJSON(json: unknown): BezierCurve {
    return BezierCurveSchema.parse(json);
  }

  // #endregion

  // #region SVG Conversion

  toSvgPathData(fractionDigits?: number, includeMove = true): string {
    const [x1, y1, x2, y2, x3, y3, x4, y4] = this.points.flatMap(p => [p.x, p.y]);
    let components: (string | number)[] = [];
    if (includeMove) {
      components = ['M', x1, y1];
    }
    components.push('C', x2, y2, x3, y3, x4, y4);
    return composeSvgPathData(components, fractionDigits);
  }

  static fromSvgPathData(pathData: string): BezierCurve {
    const tokens = tokenizeSvgPathData(pathData);
    if (tokens.length < 10) {
      // 3 for the move command, 7 for the curve command
      throw new Error('Invalid SVG path data: Not enough tokens.');
    }
    let i = 0;
    const moveTo = tokens[i++];
    if ((moveTo as string) !== 'M') {
      throw new Error('Invalid SVG path data: Expected move command.');
    }
    const x1 = tokens[i++] as number;
    const y1 = tokens[i++] as number;

    const c = tokens[i++];
    if ((c as string) !== 'C') {
      throw new Error('Invalid SVG path data: Expected curve command.');
    }
    const x2 = tokens[i++] as number;
    const y2 = tokens[i++] as number;
    const x3 = tokens[i++] as number;
    const y3 = tokens[i++] as number;
    const x4 = tokens[i++] as number;
    const y4 = tokens[i++] as number;

    return bezier([point(x1, y1), point(x2, y2), point(x3, y3), point(x4, y4)]);
  }

  // #endregion

  // #region String Conversion

  /**
   * Formats the `BezierCurve` as a string for debugging.
   */
  toString(fractionDigits?: number): string {
    return composeStruct([
      ['b', `[${this.points.map(p => p.toString(fractionDigits)).join(', ')}]`]
    ]);
  }
}
